import { useCallback, useEffect, useState } from 'react';
import {
  Copy,
  Check,
  ChevronDown,
  ChevronRight,
  Loader2,
  Plus,
  DollarSign,
  Mail,
  Link2,
} from 'lucide-react';
import {
  SuperAdminService,
  type ReferralPartnerListItem,
  type ReferralAttributionRow,
  type ReferralCommissionRow,
} from '../services/superAdminService';
import { buildReferralPartnerInviteUrl, buildReferralSignupUrl } from '../lib/referralPartner';
import { getSiteUrl } from '../lib/siteConfig';

function formatCommissionCents(cents: number, currency = 'usd'): string {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

function commissionTypeLabel(type: string): string {
  return type === 'one_time' ? 'One-time' : 'Recurring';
}

function partnerStatusBadge(status: ReferralPartnerListItem['status']) {
  if (status === 'active') {
    return { label: 'Active', className: 'bg-green-100 text-green-700' };
  }
  if (status === 'awaiting_approval') {
    return { label: 'Awaiting approval', className: 'bg-amber-100 text-amber-800' };
  }
  return { label: 'Inactive', className: 'bg-gray-100 text-gray-600' };
}

function isPartnerLinkLive(partner: ReferralPartnerListItem): boolean {
  return partner.status === 'active' && Boolean(partner.claimed_at);
}

export default function ReferralPartnersPanel() {
  const [partners, setPartners] = useState<ReferralPartnerListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [attributions, setAttributions] = useState<ReferralAttributionRow[]>([]);
  const [commissions, setCommissions] = useState<ReferralCommissionRow[]>([]);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [copiedInviteId, setCopiedInviteId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [payingOutId, setPayingOutId] = useState<string | null>(null);
  const [syncingCouponId, setSyncingCouponId] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    code: '',
    email: '',
    commission_type: 'recurring' as 'one_time' | 'recurring',
    commission_percent: '25',
    signup_discount_enabled: false,
    signup_discount_percent: '50',
    notes: '',
  });

  const loadPartners = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await SuperAdminService.listReferralPartners();
      setPartners(rows);
    } catch (e) {
      console.error('Error loading referral partners:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPartners();
  }, [loadPartners]);

  const loadDetail = async (partnerId: string) => {
    setDetailLoading(true);
    try {
      const detail = await SuperAdminService.getReferralPartnerDetail(partnerId);
      setAttributions(detail.attributions);
      setCommissions(detail.commissions);
    } catch (e) {
      console.error('Error loading partner detail:', e);
    } finally {
      setDetailLoading(false);
    }
  };

  const toggleExpand = async (partnerId: string) => {
    if (expandedId === partnerId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(partnerId);
    await loadDetail(partnerId);
  };

  const copyLink = async (partner: ReferralPartnerListItem) => {
    if (!isPartnerLinkLive(partner)) return;
    const url = buildReferralSignupUrl(partner.code, getSiteUrl());
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(partner.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      alert(url);
    }
  };

  const copyInviteLink = async (partner: ReferralPartnerListItem) => {
    const url = buildReferralPartnerInviteUrl(partner.invite_token, getSiteUrl());
    try {
      await navigator.clipboard.writeText(url);
      setCopiedInviteId(partner.id);
      setTimeout(() => setCopiedInviteId(null), 2000);
    } catch {
      alert(url);
    }
  };

  const copyInviteEmail = async (partner: ReferralPartnerListItem) => {
    const inviteUrl = buildReferralPartnerInviteUrl(partner.invite_token, getSiteUrl());
    const body = [
      `Hi ${partner.name},`,
      '',
      'Nexus Book AI is inviting you to become an official referring partner.',
      '',
      `Open your invite to review the agreement and activate your referral link:`,
      inviteUrl,
      '',
      'Once activated, you can share your unique signup link and earn commission on referred businesses.',
    ].join('\n');
    const mailto = `mailto:${encodeURIComponent(partner.email || '')}?subject=${encodeURIComponent('Nexus Referral Partner Invitation')}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
  };

  const handleCreate = async () => {
    if (!form.name.trim() || !form.code.trim()) return;
    setCreating(true);
    try {
      const { couponSyncWarning } = await SuperAdminService.createReferralPartner({
        name: form.name.trim(),
        code: form.code.trim(),
        email: form.email.trim() || undefined,
        commission_type: form.commission_type,
        commission_percent: parseFloat(form.commission_percent) || 0,
        signup_discount_enabled: form.signup_discount_enabled,
        signup_discount_percent: form.signup_discount_enabled
          ? parseFloat(form.signup_discount_percent) || 0
          : null,
        notes: form.notes.trim() || undefined,
      });
      setShowCreate(false);
      setForm({
        name: '',
        code: '',
        email: '',
        commission_type: 'recurring',
        commission_percent: '25',
        signup_discount_enabled: false,
        signup_discount_percent: '50',
        notes: '',
      });
      await loadPartners();
      if (couponSyncWarning) {
        alert(`Partner created, but signup discount setup failed:\n\n${couponSyncWarning}`);
      }
    } catch (e) {
      const message =
        e instanceof Error ? e.message : 'Failed to create partner';
      alert(message);
      await loadPartners();
    } finally {
      setCreating(false);
    }
  };

  const handlePayout = async (partnerId: string) => {
    if (!confirm('Mark all accrued commissions as paid out to this partner?')) return;
    setPayingOutId(partnerId);
    try {
      await SuperAdminService.markReferralPartnerPayout(partnerId);
      await loadPartners();
      if (expandedId === partnerId) await loadDetail(partnerId);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Payout failed');
    } finally {
      setPayingOutId(null);
    }
  };

  const handleSyncCoupon = async (partner: ReferralPartnerListItem) => {
    setSyncingCouponId(partner.id);
    try {
      await SuperAdminService.syncReferralPartnerCoupon(partner.id);
      await loadPartners();
      if (expandedId === partner.id) await loadDetail(partner.id);
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Discount sync failed');
    } finally {
      setSyncingCouponId(null);
    }
  };

  return (
    <div className="px-4 pb-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-[20px] font-bold text-gray-900">Referral partners</h2>
          <p className="text-[13px] text-gray-500 mt-0.5">
            Platform plan + add-on subscriptions only · partners must accept invite before link goes live
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-[14px] font-semibold bg-purple-600 text-white rounded-lg"
        >
          <Plus className="h-4 w-4" />
          Add partner
        </button>
      </div>

      {showCreate && (
        <div
          className="bg-white rounded-[14px] p-4 mb-4"
          style={{ boxShadow: '0 0 0 0.5px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.04)' }}
        >
          <h3 className="text-[16px] font-semibold text-gray-900 mb-3">New partner</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <input
              placeholder="Name (e.g. Colin)"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="px-3 py-2 rounded-lg border border-gray-200 text-[14px]"
            />
            <input
              placeholder="Code (e.g. colin)"
              value={form.code}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toLowerCase() }))}
              className="px-3 py-2 rounded-lg border border-gray-200 text-[14px]"
            />
            <input
              placeholder="Email (optional)"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className="px-3 py-2 rounded-lg border border-gray-200 text-[14px]"
            />
            <select
              value={form.commission_type}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  commission_type: e.target.value as 'one_time' | 'recurring',
                }))
              }
              className="px-3 py-2 rounded-lg border border-gray-200 text-[14px]"
            >
              <option value="recurring">Recurring %</option>
              <option value="one_time">One-time %</option>
            </select>
            <input
              type="number"
              min={0.01}
              max={100}
              step={0.01}
              placeholder="Commission %"
              value={form.commission_percent}
              onChange={(e) => setForm((f) => ({ ...f, commission_percent: e.target.value }))}
              className="px-3 py-2 rounded-lg border border-gray-200 text-[14px]"
            />
            <input
              placeholder="Notes (optional)"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              className="px-3 py-2 rounded-lg border border-gray-200 text-[14px] sm:col-span-2"
            />
          </div>
          <label className="flex items-center gap-3 mt-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.signup_discount_enabled}
              onChange={(e) =>
                setForm((f) => ({ ...f, signup_discount_enabled: e.target.checked }))
              }
              className="h-4 w-4 rounded border-gray-300 text-purple-600"
            />
            <span className="text-[14px] text-gray-800">
              Signup discount — first month off base platform plan
            </span>
          </label>
          {form.signup_discount_enabled ? (
            <div className="mt-2 flex items-center gap-2">
              <input
                type="number"
                min={1}
                max={100}
                step={1}
                value={form.signup_discount_percent}
                onChange={(e) =>
                  setForm((f) => ({ ...f, signup_discount_percent: e.target.value }))
                }
                className="w-24 px-3 py-2 rounded-lg border border-gray-200 text-[14px]"
              />
              <span className="text-[14px] text-gray-600">% off first month (base plan only)</span>
            </div>
          ) : null}
          <div className="flex gap-2 mt-4">
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              className="px-4 py-2 text-[14px] font-semibold bg-gray-100 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={creating}
              onClick={handleCreate}
              className="px-4 py-2 text-[14px] font-semibold bg-purple-600 text-white rounded-lg disabled:opacity-50 inline-flex items-center gap-2"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Create partner
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
        </div>
      ) : partners.length === 0 ? (
        <div className="text-center py-16 text-gray-500 text-[15px]">
          No referral partners yet. Add Colin to get started.
        </div>
      ) : (
        <div
          className="bg-white rounded-[14px] overflow-hidden"
          style={{ boxShadow: '0 0 0 0.5px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.04)' }}
        >
          {partners.map((partner, idx) => {
            const expanded = expandedId === partner.id;
            const owed = partner.accrued_cents;
            const statusBadge = partnerStatusBadge(partner.status);
            const linkLive = isPartnerLinkLive(partner);
            return (
              <div key={partner.id} className={idx > 0 ? 'border-t border-gray-100' : ''}>
                <button
                  type="button"
                  onClick={() => toggleExpand(partner.id)}
                  className="w-full px-4 py-3.5 flex items-start gap-3 text-left hover:bg-gray-50/80"
                >
                  {expanded ? (
                    <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[16px] font-semibold text-gray-900">{partner.name}</span>
                      <span className="text-[12px] font-mono text-gray-400">/{partner.code}</span>
                      <span
                        className={`text-[11px] font-bold uppercase px-1.5 py-0.5 rounded-full ${statusBadge.className}`}
                      >
                        {statusBadge.label}
                      </span>
                    </div>
                    <p className="text-[13px] text-gray-500 mt-0.5">
                      {partner.commission_percent}% {commissionTypeLabel(partner.commission_type).toLowerCase()} ·{' '}
                      {partner.signups} signup{partner.signups === 1 ? '' : 's'}
                      {partner.signup_discount_enabled && partner.signup_discount_percent
                        ? ` · ${partner.signup_discount_percent}% off month 1`
                        : ''}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-[14px] font-semibold text-gray-900">
                      {formatCommissionCents(owed)} owed
                    </p>
                    <p className="text-[12px] text-gray-400">
                      {formatCommissionCents(partner.paid_cents)} paid
                    </p>
                  </div>
                </button>

                {expanded && (
                  <div className="px-4 pb-4 pl-12 border-t border-gray-50 bg-gray-50/50">
                    <div className="flex flex-wrap gap-2 py-3">
                      {partner.status === 'awaiting_approval' ? (
                        <>
                          <button
                            type="button"
                            onClick={() => copyInviteLink(partner)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold bg-violet-600 text-white rounded-lg"
                          >
                            {copiedInviteId === partner.id ? (
                              <Check className="h-3.5 w-3.5" />
                            ) : (
                              <Link2 className="h-3.5 w-3.5" />
                            )}
                            Copy invite link
                          </button>
                          {partner.email ? (
                            <button
                              type="button"
                              onClick={() => copyInviteEmail(partner)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold bg-white border border-gray-200 rounded-lg"
                            >
                              <Mail className="h-3.5 w-3.5" />
                              Email invite
                            </button>
                          ) : null}
                        </>
                      ) : null}
                      {linkLive ? (
                        <button
                          type="button"
                          onClick={() => copyLink(partner)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold bg-white border border-gray-200 rounded-lg"
                        >
                          {copiedId === partner.id ? (
                            <Check className="h-3.5 w-3.5 text-green-600" />
                          ) : (
                            <Copy className="h-3.5 w-3.5" />
                          )}
                          Copy signup link
                        </button>
                      ) : null}
                      {partner.signup_discount_enabled &&
                      partner.signup_discount_percent &&
                      !partner.stripe_coupon_id ? (
                        <button
                          type="button"
                          disabled={syncingCouponId === partner.id}
                          onClick={() => handleSyncCoupon(partner)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold bg-amber-50 text-amber-900 border border-amber-200 rounded-lg disabled:opacity-50"
                        >
                          {syncingCouponId === partner.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : null}
                          Sync signup discount
                        </button>
                      ) : null}
                      {owed > 0 && (
                        <button
                          type="button"
                          disabled={payingOutId === partner.id}
                          onClick={() => handlePayout(partner.id)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-semibold bg-emerald-600 text-white rounded-lg disabled:opacity-50"
                        >
                          {payingOutId === partner.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <DollarSign className="h-3.5 w-3.5" />
                          )}
                          Mark payout
                        </button>
                      )}
                      {partner.status === 'active' && (
                        <button
                          type="button"
                          onClick={async () => {
                            await SuperAdminService.updateReferralPartner(partner.id, {
                              status: 'inactive',
                            });
                            await loadPartners();
                          }}
                          className="px-3 py-1.5 text-[13px] font-semibold text-red-600 bg-white border border-red-200 rounded-lg"
                        >
                          Deactivate
                        </button>
                      )}
                      {partner.status === 'awaiting_approval' && (
                        <button
                          type="button"
                          onClick={async () => {
                            await SuperAdminService.updateReferralPartner(partner.id, {
                              status: 'inactive',
                            });
                            await loadPartners();
                          }}
                          className="px-3 py-1.5 text-[13px] font-semibold text-red-600 bg-white border border-red-200 rounded-lg"
                        >
                          Cancel invite
                        </button>
                      )}
                    </div>

                    {partner.agreement_signer_name && partner.claimed_at ? (
                      <p className="text-[12px] text-gray-500 mb-3">
                        Signed by {partner.agreement_signer_name} ·{' '}
                        {new Date(partner.claimed_at).toLocaleDateString()}
                      </p>
                    ) : partner.status === 'awaiting_approval' ? (
                      <p className="text-[12px] text-amber-700/80 mb-3">
                        Invite sent — referral link activates after partner accepts the agreement.
                        {partner.signup_discount_enabled && partner.signup_discount_percent
                          ? ` New signups get ${partner.signup_discount_percent}% off the first month (base plan).`
                          : ''}
                        {partner.signup_discount_enabled &&
                        partner.signup_discount_percent &&
                        !partner.stripe_coupon_id ? (
                          <>
                            {' '}
                            Stripe coupon not configured yet — use Sync signup discount after deploying
                            stripe-subscription.
                          </>
                        ) : null}
                      </p>
                    ) : null}

                    {detailLoading ? (
                      <div className="py-6 flex justify-center">
                        <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
                      </div>
                    ) : (
                      <>
                        <p className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide mb-2">
                          Referred businesses
                        </p>
                        {attributions.length === 0 ? (
                          <p className="text-[13px] text-gray-400 mb-4">No signups yet.</p>
                        ) : (
                          <div className="rounded-lg overflow-hidden mb-4 border border-gray-200/60">
                            {attributions.map((attr, i) => (
                              <div
                                key={attr.id}
                                className={`px-3 py-2 text-[13px] bg-white ${i > 0 ? 'border-t border-gray-100' : ''}`}
                              >
                                <span className="font-medium text-gray-900">
                                  {attr.businesses?.name || attr.business_id.slice(0, 8)}
                                </span>
                                <span className="text-gray-400 ml-2 capitalize">{attr.status}</span>
                              </div>
                            ))}
                          </div>
                        )}

                        <p className="text-[12px] font-semibold text-gray-500 uppercase tracking-wide mb-2">
                          Commission ledger
                        </p>
                        {commissions.length === 0 ? (
                          <p className="text-[13px] text-gray-400">No commissions accrued yet.</p>
                        ) : (
                          <div className="rounded-lg overflow-hidden border border-gray-200/60 max-h-64 overflow-y-auto">
                            {commissions.map((comm, i) => (
                              <div
                                key={comm.id}
                                className={`px-3 py-2 bg-white flex justify-between gap-2 ${i > 0 ? 'border-t border-gray-100' : ''}`}
                              >
                                <div className="min-w-0">
                                  <p className="text-[13px] font-medium text-gray-900 truncate">
                                    {comm.businesses?.name || comm.stripe_invoice_id}
                                  </p>
                                  <p className="text-[11px] text-gray-400 capitalize">
                                    {comm.commission_kind} · {comm.status}
                                  </p>
                                </div>
                                <p className="text-[13px] font-semibold text-gray-900 flex-shrink-0">
                                  {formatCommissionCents(comm.commission_amount_cents, comm.currency)}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
